import Grid from '@mui/system/Unstable_Grid/Grid';
import React, { useEffect, useRef, useState } from 'react';
import {
  AccuracyData,
  ChartDataAdapter,
  ImageMetadata,
  VideoMetadata
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import {
  AccuracyBadge,
  ContentCardContainer,
  ContentCardHeader,
  ContentTextContainer,
  ContentAccuracyImageCard,
  ContentAccuracyVideoCard,
  ContentAccuracyImagePlaceholder,
  ContentAccuracyVideoPlaceholder,
  ContentDivider
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/UtilizationCardContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { titleToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import colors from 'src/utils/colors';

const AccuracyCard = ({
  type,
  title,
  content,
  isAccurate,
  contentRef,
  minHeight
}: {
  type: 'character' | 'image' | 'video';
  title: string;
  content: string | ImageMetadata | VideoMetadata;
  isAccurate?: boolean;
  contentRef?: React.RefObject<HTMLDivElement>;
  minHeight?: number;
}) => {
  const borderColor = (() => {
    switch (isAccurate) {
      case true:
        return '#71C184';
      case false:
        return '#FF534A';
      case null:
        return colors.brandClubYellow;
      default:
        return undefined;
    }
  })();

  return (
    <Grid ref={contentRef}>
      <ContentCardContainer sx={{ width: '504px', borderColor }}>
        <ContentCardHeader title={title}>
          {isAccurate === undefined && <div style={{ height: '32px' }} />}
          {isAccurate !== undefined && <AccuracyBadge isAccurate={isAccurate} />}
        </ContentCardHeader>
        {type === 'character' && (
          <ContentTextContainer
            textSx={{
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '24px',
              minHeight: minHeight ? `${minHeight - 120}px` : undefined,
              whiteSpace: 'pre-wrap'
            }}
          >
            <>{content || (`Missing required ${title.toLocaleLowerCase()}.` as string)}</>
          </ContentTextContainer>
        )}
        {type === 'image' && (
          <>
            {content ? (
              <ContentAccuracyImageCard
                imageUri={(content as ImageMetadata).url}
                mainDisplayTitle={(content as ImageMetadata).altText}
                secondaryDisplayTitle={(content as ImageMetadata).altText}
                metrics={{
                  resolution: (content as ImageMetadata).resolution,
                  ratio: (content as ImageMetadata).ratio
                }}
              />
            ) : (
              <ContentAccuracyImagePlaceholder />
            )}
          </>
        )}
        {type === 'video' && (
          <>
            {content ? (
              <ContentAccuracyVideoCard
                videoSrc={(content as VideoMetadata).url}
                metrics={{
                  resolution: (content as VideoMetadata).resolution,
                  length: (content as VideoMetadata).length
                }}
              />
            ) : (
              <ContentAccuracyVideoPlaceholder />
            )}
          </>
        )}
      </ContentCardContainer>
    </Grid>
  );
};

const ContentAccuracySectionHeaders = () => (
  <>
    <Grid xs={6}>
      <Text sx={{ height: '28px', fontWeight: '500', fontSize: '18px' }}>Master Content</Text>
    </Grid>
    <Grid xs={6}>
      {/* Offset by half column gap (13px) to align with grid */}
      <Text sx={{ marginLeft: '13px', height: '28px', fontWeight: '500', fontSize: '18px' }}>Current Content</Text>
    </Grid>
  </>
);

const AccuracyComparisonRow = ({ item }: { item: AccuracyData }) => {
  const leftCardRef = useRef<HTMLDivElement>(null);
  const rightCardRef = useRef<HTMLDivElement>(null);
  const [synchronizedHeight, setSynchronizedHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const calculateMaxHeight = () => {
      const leftContainer = leftCardRef.current?.querySelector('.ContentTextContainer');
      const rightContainer = rightCardRef.current?.querySelector('.ContentTextContainer');

      if (!leftContainer || !rightContainer) {
        return;
      }

      (leftContainer as HTMLElement).style.minHeight = '';
      (rightContainer as HTMLElement).style.minHeight = '';

      const leftHeight = leftContainer.getBoundingClientRect().height;
      const rightHeight = rightContainer.getBoundingClientRect().height;

      const maxHeight = Math.max(leftHeight, rightHeight);
      if (maxHeight > 0 && maxHeight !== synchronizedHeight) {
        (leftContainer as HTMLElement).style.minHeight = `${maxHeight}px`;
        (rightContainer as HTMLElement).style.minHeight = `${maxHeight}px`;
        setSynchronizedHeight(maxHeight);
      }
    };

    const timeoutId = setTimeout(calculateMaxHeight, 0);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);
  return (
    <Flex id={titleToId(item.title)} gap={32}>
      <AccuracyCard type={item.type} title={item.title} content={item.approvedContent} contentRef={leftCardRef} />
      <AccuracyCard
        type={item.type}
        title={item.title}
        content={item.currentContent}
        contentRef={rightCardRef}
        isAccurate={item.isAccurate}
      />
    </Flex>
  );
};

interface ProductLevelContentAccuracySummaryProps {
  queryFn: () => Promise<ChartDataAdapter<AccuracyData[]>>;
  renderSubtitle?: () => string | React.ReactNode;
}

export const ProductLevelContentAccuracySummary = ({
  queryFn,
  renderSubtitle
}: ProductLevelContentAccuracySummaryProps) => {
  return (
    <BeaconProChart name="product-level-content-accuracy-summary" queryFn={queryFn}>
      <BeaconProChart.Header menuButton={null}>
        <BeaconProChart.Title>
          <Text variant="h4">Content Accuracy by Element</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle} />
      <BeaconProChart.Visualization
        render={(adapter: ChartDataAdapter<AccuracyData[]>) => {
          const data = adapter.transform();

          return (
            <Grid container justifyContent="center" sx={{ marginTop: '32px' }}>
              <ContentAccuracySectionHeaders />
              <Flex flexDirection="column" style={{ marginTop: '0px' }}>
                {data.map((item: AccuracyData, index: number) => {
                  // for the first image in images section, we do not want to offset by -32px
                  const isLastItem = index === data.length - 1;
                  const nextItem = isLastItem ? null : data[index + 1];
                  const prevItem = index === 0 ? null : data[index - 1];
                  const isFirstImageInSequence = item.type === 'image' && (!prevItem || prevItem.type !== 'image');

                  // divider is only between sections of different data types
                  let shouldShowDivider = !isLastItem;
                  if (item.type === 'image') {
                    shouldShowDivider = !isLastItem && nextItem && item.type !== nextItem.type;
                  }

                  return (
                    <React.Fragment key={item.title}>
                      <Flex sx={item.type === 'image' && !isFirstImageInSequence ? { marginTop: '-32px' } : {}}>
                        <AccuracyComparisonRow item={item} />
                      </Flex>
                      {shouldShowDivider && (
                        <Flex justifyContent="center">
                          <ContentDivider />
                        </Flex>
                      )}
                    </React.Fragment>
                  );
                })}
              </Flex>
            </Grid>
          );
        }}
      />
    </BeaconProChart>
  );
};
